import { adminConsoleClient } from './adminConsoleClient';
import { clearAdminConsoleToken, setAdminConsoleToken } from '../lib/adminConsoleToken';
import { isAxiosNetworkUnreachableError } from '../lib/axiosErrorMessage';
import { filenameFromContentDisposition } from '../lib/adminIamQuery';
import { csvDataRowCount, isExportTruncated, type CsvExportResult } from '../lib/adminCsvDownload';
import { AUDIT_ACTIONS } from '../lib/adminAuditPresentation';

const ADMIN_API_TRANSIENT_RETRY_ATTEMPTS = 3;
const ADMIN_API_TRANSIENT_RETRY_DELAY_MS = 600;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function withTransientAdminApiRetry<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < ADMIN_API_TRANSIENT_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      const canRetry =
        isAxiosNetworkUnreachableError(err) && attempt < ADMIN_API_TRANSIENT_RETRY_ATTEMPTS - 1;
      if (!canRetry) break;
      await wait(ADMIN_API_TRANSIENT_RETRY_DELAY_MS * (attempt + 1));
    }
  }
  throw lastError;
}

export type AdminRole = 'SUPER_ADMIN' | 'EMPLOYEE';

/** Every admin-api audit action, with display labels in `lib/adminAuditPresentation`. */
export const ADMIN_AUDIT_ACTIONS = AUDIT_ACTIONS;

export type AdminAuditAction = (typeof ADMIN_AUDIT_ACTIONS)[number];

export type AdminConsoleGroupRef = { id: string; name: string };

/** Which model decides access: v1 groups, or v2 role assignments (RBAC Phase 1). */
export type AdminAuthzModel = 'v1' | 'v2';

/**
 * Resolved access from `GET /auth/me`. `permissions` are always v2 catalog keys; in v1 mode
 * admin-api translates them from the user's groups so decisions are identical.
 */
export type AdminConsoleAccess = {
  role: string | null;
  roles: Array<{ key: string; name: string }>;
  permissions: string[];
  mfaRequired: string[];
  catalogVersion: number;
};

export type AdminIamRoleRef = { id: string; key: string; name: string };

/** RBAC Phase 2: `expired` once `expiresAt <= now`, `expiring` within 7 days, else `active`. */
export type AdminAssignmentStatus = 'active' | 'expiring' | 'expired';

/** A role on a `GET /admin/users` item (Phase 2 adds the assignment id, status and expiry). */
export type AdminUserRoleRef = AdminIamRoleRef & {
  assignmentId?: string;
  status?: AdminAssignmentStatus;
  expiresAt?: string | null;
};

/** `GET /auth/me` → `mfa` (Phase 2). */
export type AdminConsoleMfaState = {
  verified: boolean;
  verifiedAt?: string | null;
  expiresAt?: string | null;
  reason?: string;
};

export type AdminConsoleUser = {
  id: string;
  email: string;
  displayName: string;
  role: AdminRole;
  isActive: boolean;
  isMonitored: boolean;
  lastLoginAt?: string | null;
  groups?: AdminConsoleGroupRef[];
  /** v1 group keys (legacy). Access decisions read `access.permissions` instead. */
  permissions?: string[];
  /** Role assignments (`GET /admin/users` items). */
  roles?: AdminUserRoleRef[];
  /** Worst non-`none` assignment status across the user's roles (`GET /admin/users` items). */
  assignmentStatus?: 'none' | AdminAssignmentStatus;
  createdAt?: string | null;
  /** Only on `GET /auth/me` (Phase 2). */
  mfa?: AdminConsoleMfaState;
  /** Only on `GET /auth/me`. */
  authzModel?: AdminAuthzModel;
  /** Only on `GET /auth/me`. */
  access?: AdminConsoleAccess;
};

export type AdminConsoleGroup = {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  memberCount?: number;
  permissions: string[];
  members?: Array<{ id: string; email: string; displayName: string; isActive: boolean }>;
};

export type AdminPermissionCatalogItem = {
  key: string;
  label: string;
  group: string;
};

export type AdminConsoleLoginResponse = {
  token: string;
  user: AdminConsoleUser;
  mfa?: { verified: boolean; reason?: string };
};

export const adminConsoleAuthApi = {
  async login(email: string, password: string, mfaAttestation?: string): Promise<AdminConsoleLoginResponse> {
    return withTransientAdminApiRetry(async () => {
      const res = await adminConsoleClient.post<AdminConsoleLoginResponse>('/auth/login', {
        email,
        password,
        ...(mfaAttestation ? { mfaAttestation } : {}),
      });
      if (res.data.token) setAdminConsoleToken(res.data.token);
      return res.data;
    });
  },

  async provisionOperator(input: {
    email: string;
    password: string;
    displayName?: string;
    name?: string;
  }): Promise<{ provisioned: boolean; alreadyExists: boolean }> {
    return withTransientAdminApiRetry(async () => {
      const res = await adminConsoleClient.post<{
        provisioned: boolean;
        alreadyExists: boolean;
      }>('/auth/provision-operator', input);
      return res.data;
    });
  },

  async ensureOperatorAccount(input: {
    email: string;
    password: string;
    displayName?: string;
    name?: string;
    mfaAttestation?: string;
  }): Promise<AdminConsoleLoginResponse> {
    const { mfaAttestation, ...provision } = input;
    await adminConsoleAuthApi.provisionOperator(provision);
    return adminConsoleAuthApi.login(input.email, input.password, mfaAttestation);
  },

  async me(): Promise<AdminConsoleUser> {
    const res = await adminConsoleClient.get<AdminConsoleUser>('/auth/me');
    return res.data;
  },

  logout(): void {
    clearAdminConsoleToken();
  },
};

export const LOAN_REVIEW_SHIFT_QUERY_KEY = ['loanReviewShift'] as const;

export type LoanReviewShiftState = {
  isLoanReviewer: boolean;
  onShift: boolean;
  shiftStartedAt?: string | null;
};

export type LoanReviewShiftEndResponse = {
  onShift: false;
  alreadyClosed: boolean;
  releasedApplicationId: string | null;
  handbackConfirmed?: boolean;
};

export const loanReviewShiftApi = {
  async current(): Promise<LoanReviewShiftState> {
    const res = await adminConsoleClient.get<LoanReviewShiftState>('/loan-review/shift');
    return res.data;
  },
  async start(): Promise<{ onShift: true; alreadyOpen: boolean; shiftStartedAt: string }> {
    const res = await adminConsoleClient.post<{
      onShift: true;
      alreadyOpen: boolean;
      shiftStartedAt: string;
    }>('/loan-review/shift/start');
    return res.data;
  },
  async end(): Promise<LoanReviewShiftEndResponse> {
    const res = await adminConsoleClient.post<LoanReviewShiftEndResponse>('/loan-review/shift/end');
    return res.data;
  },
};

/** `GET /admin/users` query (Phase 2). `sort` is `<field>:<asc|desc>`. */
export type AdminUsersListParams = {
  q?: string;
  status?: 'active' | 'inactive' | 'never_signed_in';
  roleId?: string;
  sort?: string;
  limit?: number;
  offset?: number;
};

export const adminConsoleUsersApi = {
  async list(params: AdminUsersListParams): Promise<{ total: number; items: AdminConsoleUser[] }> {
    const res = await adminConsoleClient.get<{ total: number; items: AdminConsoleUser[] }>('/admin/users', {
      params,
    });
    return res.data;
  },

  async get(id: string): Promise<AdminConsoleUser> {
    const res = await adminConsoleClient.get<AdminConsoleUser>(`/admin/users/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(body: {
    email: string;
    password: string;
    displayName: string;
    groupIds?: string[];
  }): Promise<AdminConsoleUser> {
    const res = await adminConsoleClient.post<AdminConsoleUser>('/admin/users', body);
    return res.data;
  },

  async patch(
    id: string,
    body: Partial<{ displayName: string; isActive: boolean; isMonitored: boolean; password: string; groupIds: string[] }>,
  ): Promise<AdminConsoleUser> {
    const res = await adminConsoleClient.patch<AdminConsoleUser>(`/admin/users/${encodeURIComponent(id)}`, body);
    return res.data;
  },

  async remove(id: string): Promise<void> {
    await adminConsoleClient.delete(`/admin/users/${encodeURIComponent(id)}`);
  },

  /**
   * RBAC Phase 4: a one-time passkey enrolment code for the user's first passkey. The response is
   * the only time the code is shown; admin-api does not keep it.
   */
  async issuePasskeyEnrolmentToken(id: string, reason?: string): Promise<AdminPasskeyEnrolmentToken> {
    const res = await adminConsoleClient.post<AdminPasskeyEnrolmentToken>(
      `/admin/users/${encodeURIComponent(id)}/passkey-enrolment-token`,
      reason ? { reason } : {},
    );
    return res.data;
  },
};

/** `POST /admin/users/:id/passkey-enrolment-token` → 201. */
export type AdminPasskeyEnrolmentToken = { token: string; expiresAt: string };

export const adminConsoleGroupsApi = {
  async catalog(): Promise<{ items: AdminPermissionCatalogItem[] }> {
    const res = await adminConsoleClient.get<{ items: AdminPermissionCatalogItem[] }>('/admin/groups/catalog');
    return res.data;
  },

  async list(): Promise<{ total: number; items: AdminConsoleGroup[] }> {
    const res = await adminConsoleClient.get<{ total: number; items: AdminConsoleGroup[] }>('/admin/groups');
    return res.data;
  },

  async get(id: string): Promise<AdminConsoleGroup> {
    const res = await adminConsoleClient.get<AdminConsoleGroup>(`/admin/groups/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(body: {
    name: string;
    description?: string;
    permissions: string[];
  }): Promise<AdminConsoleGroup> {
    const res = await adminConsoleClient.post<AdminConsoleGroup>('/admin/groups', body);
    return res.data;
  },

  async patch(
    id: string,
    body: Partial<{ name: string; description: string; permissions: string[] }>,
  ): Promise<AdminConsoleGroup> {
    const res = await adminConsoleClient.patch<AdminConsoleGroup>(`/admin/groups/${encodeURIComponent(id)}`, body);
    return res.data;
  },

  async remove(id: string): Promise<void> {
    await adminConsoleClient.delete(`/admin/groups/${encodeURIComponent(id)}`);
  },
};

export type AdminIamPermission = {
  key: string;
  label: string;
  group: string;
  risk: 'standard' | 'sensitive' | 'critical';
  requiresMfa: boolean;
  deprecatedAt: string | null;
};

export type AdminIamRole = {
  id: string;
  key: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: string[];
  assignmentCount: number;
};

export type AdminIamRoleMember = {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  grantedBy: string | null;
  grantedAt: string;
  expiresAt: string | null;
  reason: string | null;
  status?: AdminAssignmentStatus;
};

export type AdminIamRoleDetail = AdminIamRole & { assignments: AdminIamRoleMember[] };

export type AdminIamUserAssignment = {
  id: string;
  role: AdminIamRoleRef;
  grantedBy: string | null;
  grantedAt: string;
  expiresAt: string | null;
  reason: string | null;
  status?: AdminAssignmentStatus;
};

/** `GET /admin/iam/assignments` item. */
export type AdminIamAssignmentListItem = {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  role: AdminIamRoleRef;
  grantedBy: string | null;
  grantedAt: string;
  expiresAt: string | null;
  reason: string | null;
  status: AdminAssignmentStatus;
};

export type AdminIamAssignmentsParams = {
  roleId?: string;
  userId?: string;
  status?: AdminAssignmentStatus | 'all';
  /** ISO; rows with `expiresAt` before this. */
  expiresBefore?: string;
  /** ISO; rows with `expiresAt` after this. */
  expiresAfter?: string;
  /** Default: newest grant first. `expiresAt` = soonest expiry first. */
  sort?: 'expiresAt';
  limit?: number;
  offset?: number;
};

export type AdminEffectivePermission = {
  key: string;
  label: string;
  group: string;
  risk: AdminIamPermission['risk'];
  requiresMfa: boolean;
  grantedBy: Array<{ roleId: string; roleKey: string; roleName: string }>;
  /** v1 groups that grant the key (admin-api with group sources; absent on older builds). */
  grantedByGroups?: Array<{ groupId: string; groupName: string }>;
};

export type AdminEffectivePermissions = {
  userId: string;
  model: AdminAuthzModel;
  decided: string[];
  /** The user is a Super Admin (v1: every key comes from the role itself). */
  superAdmin?: boolean;
  /** The user's v1 groups (empty under v2). */
  groups?: Array<{ id: string; name: string }>;
  permissions: AdminEffectivePermission[];
  /** Catalog keys the user does not hold, so the console can show what they cannot do. */
  notHeld?: Array<Omit<AdminEffectivePermission, 'grantedBy' | 'grantedByGroups'>>;
};

export type AdminRolePreview = {
  added: string[];
  removed: string[];
  affectedHolders: number;
  violation: { status: number; code: string; error: string } | null;
};

export type AdminRoleInput = {
  key?: string;
  name: string;
  description?: string;
  permissions: string[];
};

/**
 * IAM API (RBAC Phase 1). Read-only role views plus role assignment; role create/edit is Phase 2.
 * Errors are `{ error, code? }`; show `error` via `messageFromAdminConsoleAxiosError`.
 */
export const adminIamApi = {
  async permissions(): Promise<{ items: AdminIamPermission[] }> {
    const res = await adminConsoleClient.get<{ items: AdminIamPermission[] }>('/admin/iam/permissions');
    return res.data;
  },

  async roles(): Promise<{ items: AdminIamRole[] }> {
    const res = await adminConsoleClient.get<{ items: AdminIamRole[] }>('/admin/iam/roles');
    return res.data;
  },

  async role(id: string): Promise<AdminIamRoleDetail> {
    const res = await adminConsoleClient.get<AdminIamRoleDetail>(`/admin/iam/roles/${encodeURIComponent(id)}`);
    return res.data;
  },

  async createRole(body: AdminRoleInput): Promise<AdminIamRole> {
    const res = await adminConsoleClient.post<AdminIamRole>('/admin/iam/roles', body);
    return res.data;
  },

  async updateRole(id: string, body: Partial<Omit<AdminRoleInput, 'key'>>): Promise<AdminIamRole> {
    const res = await adminConsoleClient.patch<AdminIamRole>(`/admin/iam/roles/${encodeURIComponent(id)}`, body);
    return res.data;
  },

  async deleteRole(id: string): Promise<void> {
    await adminConsoleClient.delete(`/admin/iam/roles/${encodeURIComponent(id)}`);
  },

  /** Dry run of a permission change: saves nothing. */
  async previewRole(id: string, permissions: string[]): Promise<AdminRolePreview> {
    const res = await adminConsoleClient.post<AdminRolePreview>(
      `/admin/iam/roles/${encodeURIComponent(id)}/preview`,
      { permissions },
    );
    return res.data;
  },

  async userAssignments(userId: string): Promise<{ items: AdminIamUserAssignment[] }> {
    const res = await adminConsoleClient.get<{ items: AdminIamUserAssignment[] }>(
      `/admin/iam/users/${encodeURIComponent(userId)}/assignments`,
    );
    return res.data;
  },

  async effectivePermissions(userId: string): Promise<AdminEffectivePermissions> {
    const res = await adminConsoleClient.get<AdminEffectivePermissions>(
      `/admin/iam/users/${encodeURIComponent(userId)}/effective-permissions`,
    );
    return res.data;
  },

  async assignments(
    params: AdminIamAssignmentsParams,
  ): Promise<{ total: number; items: AdminIamAssignmentListItem[] }> {
    const res = await adminConsoleClient.get<{ total: number; items: AdminIamAssignmentListItem[] }>(
      '/admin/iam/assignments',
      { params },
    );
    return res.data;
  },

  /** `reason` is required (Phase 2); Super Admin must have no `expiresAt`. */
  async assign(body: {
    userId: string;
    roleId: string;
    reason: string;
    expiresAt?: string;
  }): Promise<AdminIamUserAssignment> {
    const res = await adminConsoleClient.post<AdminIamUserAssignment>('/admin/iam/assignments', body);
    return res.data;
  },

  async updateAssignment(
    assignmentId: string,
    body: { expiresAt?: string | null; reason?: string },
  ): Promise<AdminIamUserAssignment> {
    const res = await adminConsoleClient.patch<AdminIamUserAssignment>(
      `/admin/iam/assignments/${encodeURIComponent(assignmentId)}`,
      body,
    );
    return res.data;
  },

  async unassign(assignmentId: string): Promise<void> {
    await adminConsoleClient.delete(`/admin/iam/assignments/${encodeURIComponent(assignmentId)}`);
  },
};

export type AdminReviewStatus = 'open' | 'closed';
export type AdminReviewDecision = 'keep' | 'revoke';
export type AdminReviewOutcome = 'kept' | 'revoked' | 'undecided' | 'revoke_failed';

export type AdminAccessReview = {
  id: string;
  name: string;
  description?: string | null;
  status: AdminReviewStatus;
  dueAt: string;
  autoRevoke: boolean;
  createdBy: string | null;
  createdAt: string;
  closedAt: string | null;
  counts: { total: number; keep: number; revoke: number; undecided: number };
};

export type AdminAccessReviewItem = {
  id: string;
  userId: string;
  userEmail: string;
  roleId: string;
  roleKey: string;
  roleName: string;
  roleRisk: AdminIamPermission['risk'] | null;
  userLastLoginAt: string | null;
  reviewerUserId: string | null;
  reviewerEmail: string | null;
  decision: AdminReviewDecision | null;
  comment: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  outcome: AdminReviewOutcome | null;
  outcomeDetail: string | null;
};

export type AdminAccessReviewCreate = {
  name: string;
  description?: string;
  dueAt: string;
  scope: { roleIds?: string[]; minRisk?: 'sensitive' | 'critical'; userIds?: string[] };
  reviewerUserId?: string;
  autoRevoke?: boolean;
};

/** Access reviews (RBAC Phase 2 item 8). */
export const adminAccessReviewsApi = {
  async list(): Promise<{ items: AdminAccessReview[] }> {
    const res = await adminConsoleClient.get<{ items: AdminAccessReview[] }>('/admin/iam/reviews');
    return res.data;
  },

  async create(body: AdminAccessReviewCreate): Promise<AdminAccessReview> {
    const res = await adminConsoleClient.post<AdminAccessReview>('/admin/iam/reviews', body);
    return res.data;
  },

  async get(
    id: string,
    decision?: AdminReviewDecision | 'none',
  ): Promise<{ review: AdminAccessReview; items: AdminAccessReviewItem[] }> {
    const res = await adminConsoleClient.get<{ review: AdminAccessReview; items: AdminAccessReviewItem[] }>(
      `/admin/iam/reviews/${encodeURIComponent(id)}`,
      { params: decision ? { decision } : undefined },
    );
    return res.data;
  },

  async decide(
    id: string,
    itemId: string,
    body: { decision: AdminReviewDecision; comment?: string },
  ): Promise<AdminAccessReviewItem> {
    const res = await adminConsoleClient.post<AdminAccessReviewItem>(
      `/admin/iam/reviews/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/decision`,
      body,
    );
    return res.data;
  },

  async reassign(id: string, itemId: string, reviewerUserId: string): Promise<AdminAccessReviewItem> {
    const res = await adminConsoleClient.patch<AdminAccessReviewItem>(
      `/admin/iam/reviews/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`,
      { reviewerUserId },
    );
    return res.data;
  },

  async close(id: string): Promise<AdminAccessReview> {
    const res = await adminConsoleClient.post<AdminAccessReview>(
      `/admin/iam/reviews/${encodeURIComponent(id)}/close`,
    );
    return res.data;
  },

  /** CSV evidence of every item, downloaded with the Bearer header. */
  async exportCsv(id: string): Promise<CsvExportResult> {
    return downloadAdminCsv(
      `/admin/iam/reviews/${encodeURIComponent(id)}/export`,
      { format: 'csv' },
      `access-review-${id}.csv`,
    );
  },
};

// ── Just-in-time access (RBAC Phase 4 workstream A) ─────────────────────────

/** `IAM_JIT_MODE` on admin-api: off (no activations), report_only, enforce (critical roles eligible-only). */
export type AdminJitMode = 'off' | 'report_only' | 'enforce';

/** Stored status, plus `expired` derived by admin-api from the clock. */
export type AdminJitStatus = 'pending' | 'active' | 'rejected' | 'cancelled' | 'ended' | 'expired';

export type AdminJitActivation = {
  id: string;
  userId: string;
  roleId: string;
  roleKey: string;
  status: Exclude<AdminJitStatus, 'expired'>;
  displayStatus: AdminJitStatus;
  reason: string | null;
  ticket: string | null;
  durationMinutes: number;
  requiresApproval: boolean;
  approverRoleId: string | null;
  requestedAt: string;
  decidedByEmail: string | null;
  decisionComment: string | null;
  assignmentId: string | null;
  /** Recorded over a standing assignment (report-only rollout): no new assignment was written. */
  reportOnly: boolean;
  activatedAt: string | null;
  expiresAt: string | null;
  endedAt: string | null;
  /** Set on `GET /admin/iam/activations` rows (not on single-request responses). */
  userEmail?: string | null;
  userDisplayName?: string | null;
  roleName?: string | null;
};

export type AdminJitEligibility = {
  id: string;
  userId: string;
  roleId: string;
  maxDurationMinutes: number;
  requiresApproval: boolean;
  approverRoleId: string | null;
  reasonRequired: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
};

/** `GET /admin/iam/eligibilities` rows name their user, role and approver role. */
export type AdminJitEligibilityListItem = AdminJitEligibility & {
  userEmail: string | null;
  userDisplayName: string | null;
  roleKey: string | null;
  roleName: string | null;
  approverRoleName: string | null;
};

export type AdminJitMyAccess = {
  mode: AdminJitMode;
  eligibilities: Array<
    AdminJitEligibility & {
      roleKey: string;
      roleName: string;
      activation: AdminJitActivation | null;
      /** RBAC Phase 4: activating needs a passkey sign-in this session lacks (older admin-api: absent). */
      passkeyRequired?: boolean;
    }
  >;
};

export type AdminJitActivationRequest = { roleId: string; reason?: string; ticket?: string; durationMinutes?: number };

export type AdminJitEligibilityInput = {
  userId: string;
  roleId: string;
  maxDurationMinutes: number;
  requiresApproval: boolean;
  approverRoleId: string | null;
  reasonRequired: boolean;
};

export const ADMIN_JIT_MY_ACCESS_QUERY_KEY = ['adminJitMyAccess'] as const;
export const ADMIN_JIT_ACTIVATIONS_QUERY_KEY = ['adminJitActivations'] as const;
export const ADMIN_JIT_ELIGIBILITIES_QUERY_KEY = ['adminJitEligibilities'] as const;

/** Eligibilities and activations (`/admin/iam/me/access`, `/admin/iam/activations`, `/admin/iam/eligibilities`). */
export const adminJitApi = {
  async myAccess(): Promise<AdminJitMyAccess> {
    const res = await adminConsoleClient.get<AdminJitMyAccess>('/admin/iam/me/access');
    return res.data;
  },

  async activations(scope: 'mine' | 'approvable' | 'all'): Promise<{ items: AdminJitActivation[] }> {
    const res = await adminConsoleClient.get<{ items: AdminJitActivation[] }>('/admin/iam/activations', { params: { scope } });
    return res.data;
  },

  async request(body: AdminJitActivationRequest): Promise<AdminJitActivation> {
    const res = await adminConsoleClient.post<AdminJitActivation>('/admin/iam/activations', body);
    return res.data;
  },

  async decide(id: string, decision: 'approve' | 'reject', comment?: string): Promise<AdminJitActivation> {
    const res = await adminConsoleClient.post<AdminJitActivation>(
      `/admin/iam/activations/${encodeURIComponent(id)}/${decision}`,
      comment ? { comment } : {},
    );
    return res.data;
  },

  async cancel(id: string): Promise<AdminJitActivation> {
    const res = await adminConsoleClient.post<AdminJitActivation>(`/admin/iam/activations/${encodeURIComponent(id)}/cancel`);
    return res.data;
  },

  async end(id: string): Promise<AdminJitActivation> {
    const res = await adminConsoleClient.post<AdminJitActivation>(`/admin/iam/activations/${encodeURIComponent(id)}/end`);
    return res.data;
  },

  async eligibilities(params: { userId?: string; roleId?: string } = {}): Promise<{ items: AdminJitEligibilityListItem[] }> {
    const res = await adminConsoleClient.get<{ items: AdminJitEligibilityListItem[] }>('/admin/iam/eligibilities', { params });
    return res.data;
  },

  async createEligibility(body: AdminJitEligibilityInput): Promise<AdminJitEligibility> {
    const res = await adminConsoleClient.post<AdminJitEligibility>('/admin/iam/eligibilities', body);
    return res.data;
  },

  async updateEligibility(id: string, body: Omit<AdminJitEligibilityInput, 'userId' | 'roleId'>): Promise<AdminJitEligibility> {
    const res = await adminConsoleClient.patch<AdminJitEligibility>(`/admin/iam/eligibilities/${encodeURIComponent(id)}`, body);
    return res.data;
  },

  async deleteEligibility(id: string): Promise<void> {
    await adminConsoleClient.delete(`/admin/iam/eligibilities/${encodeURIComponent(id)}`);
  },
};

export type AdminAuditListItem = {
  id: string;
  occurredAt: string | null;
  actorUserId: string | null;
  actorEmail: string;
  action: AdminAuditAction;
  entityType: string;
  entityId: string;
  requestId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
};

/** `GET /admin/audit-logs` query (Phase 2), built by `buildAuditQuery`. */
export type AdminAuditQueryParams = Record<string, string | number>;

export const adminConsoleAuditApi = {
  async list(
    params: AdminAuditQueryParams,
  ): Promise<{ items: AdminAuditListItem[]; nextCursor: string | null }> {
    const res = await adminConsoleClient.get<{ items: AdminAuditListItem[]; nextCursor: string | null }>(
      '/admin/audit-logs',
      { params },
    );
    return { items: res.data.items ?? [], nextCursor: res.data.nextCursor ?? null };
  },

  /** Same filters as `list`, as CSV (at most 10,000 rows). */
  async exportCsv(params: AdminAuditQueryParams): Promise<CsvExportResult> {
    const { cursor: _cursor, limit: _limit, ...filters } = params;
    return downloadAdminCsv('/admin/audit-logs', { ...filters, format: 'csv' }, 'admin-audit-log.csv');
  },
};

/**
 * Downloads a CSV from admin-api through the authenticated client (a plain link would not carry
 * the Bearer header) and saves it under the server's `Content-Disposition` filename when given.
 * Error bodies arrive as Blobs; the admin-api client interceptor parses them back to JSON, so
 * rejections carry `response.data.code` like any other call. Reports `X-Truncated: true` (the
 * server's export row cap) so the page can warn.
 */
async function downloadAdminCsv(
  path: string,
  params: Record<string, string | number>,
  fallbackName: string,
): Promise<CsvExportResult> {
  const res = await adminConsoleClient.get<Blob>(path, { params, responseType: 'blob' });
  const header = res.headers?.['content-disposition'];
  const name = filenameFromContentDisposition(typeof header === 'string' ? header : null) ?? fallbackName;
  const blob = res.data instanceof Blob ? res.data : new Blob([String(res.data)], { type: 'text/csv' });
  const truncated = isExportTruncated(res.headers as Record<string, unknown> | undefined);
  const rows = truncated ? csvDataRowCount(await blob.text()) : 0;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }
  return { truncated, rows };
}
